import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const PAGE_SIZE = 20; // matches DRF DEFAULT_PAGINATION_CLASS PAGE_SIZE

/**
 * Page-number pagination for paginated list endpoints.
 *
 * Backwards-compatible with the previous {next, previous, onNext, onPrev} API:
 * if `count` and `page` aren't supplied, falls back to a simple Prev/Next bar.
 *
 *   <Pagination count={data.count} page={page} onPage={setPage} />        // rich
 *   <Pagination next={...} previous={...} onNext={...} onPrev={...} />    // legacy
 */
export default function Pagination({
  count,
  page,
  onPage,
  pageSize = PAGE_SIZE,
  // Legacy fallback props
  next,
  previous,
  onNext,
  onPrev,
}) {
  const richMode = typeof count === "number" && typeof page === "number" && typeof onPage === "function";

  if (!richMode) {
    return (
      <div className="flex justify-end gap-2 mt-6">
        <button
          disabled={!previous}
          onClick={onPrev}
          className="px-3 py-1.5 rounded-md flex items-center gap-1 text-sm border border-border-default text-text-secondary hover:text-text-primary hover:border-button-primary/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4" />
          Previous
        </button>
        <button
          disabled={!next}
          onClick={onNext}
          className="px-3 py-1.5 rounded-md flex items-center gap-1 text-sm border border-border-default text-text-secondary hover:text-text-primary hover:border-button-primary/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <ChevronRight className="w-4" />
        </button>
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);

  const window = pageWindow(safePage, pageCount);

  const [jump, setJump] = useState(String(safePage));
  useEffect(() => { setJump(String(safePage)); }, [safePage]);

  const go = (p) => onPage(Math.min(Math.max(1, p), pageCount));

  const btn = (label, onClick, opts = {}) => (
    <button
      key={opts.key ?? label}
      onClick={onClick}
      disabled={opts.disabled}
      className={`min-w-[34px] h-9 px-2 text-sm rounded-md border transition-colors flex items-center justify-center gap-1 ${
        opts.active
          ? "bg-button-primary border-button-primary text-white font-semibold"
          : "border-border-default text-text-secondary hover:text-text-primary hover:border-button-primary/40"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
      <div className="text-xs text-text-secondary">
        Page <span className="text-text-primary font-medium">{safePage}</span> of{" "}
        <span className="text-text-primary font-medium">{pageCount.toLocaleString()}</span>
        {count > 0 && (
          <>
            {" · "}
            <span className="text-text-primary font-medium">{count.toLocaleString()}</span> total
          </>
        )}
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        {btn(<ChevronsLeft className="w-4" />, () => go(1), { disabled: safePage === 1, key: "first" })}
        {btn(<ChevronLeft className="w-4" />,  () => go(safePage - 1), { disabled: safePage === 1, key: "prev" })}

        {window[0] > 1 && (
          <span className="text-text-secondary px-1 text-sm">…</span>
        )}
        {window.map((p) =>
          btn(String(p), () => go(p), { active: p === safePage, key: `p-${p}` })
        )}
        {window[window.length - 1] < pageCount && (
          <span className="text-text-secondary px-1 text-sm">…</span>
        )}

        {btn(<ChevronRight className="w-4" />, () => go(safePage + 1), { disabled: safePage === pageCount, key: "next" })}
        {btn(<ChevronsRight className="w-4" />, () => go(pageCount), { disabled: safePage === pageCount, key: "last" })}

        <form
          onSubmit={(e) => { e.preventDefault(); const n = parseInt(jump, 10); if (!Number.isNaN(n)) go(n); }}
          className="ml-3 flex items-center gap-1.5"
        >
          <span className="text-xs text-text-secondary">Go to</span>
          <input
            type="number"
            min={1}
            max={pageCount}
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            className="w-16 bg-bg-main border border-border-default rounded-md py-1 px-2 text-sm text-text-primary text-center focus:outline-none focus:border-button-primary"
          />
        </form>
      </div>
    </div>
  );
}

// Build a list of up to MAX page numbers around `current`.
function pageWindow(current, total) {
  const MAX = 7;
  if (total <= MAX) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(MAX / 2);
  let start = Math.max(1, current - half);
  let end = start + MAX - 1;
  if (end > total) {
    end = total;
    start = end - MAX + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}
